import {
  IPendingClientApiCall,
  IRestApiService,
  PendingClientDTO,
  PendingClientFilter,
  PendingClientDataDTO,
  SaleHistoryItemDTO,
  SaleHistoryDTO,
  PendingClientDashboardParams,
  ClientInfo,
  SpreadsheetMapping,
  SpreadsheetConfig,
  getSourseDTO,
  LeadsKanbanViewFilter,
  LeadsKanbanViewDto,
  LeadKanbanDto,
  BulkUpdatePendingClientDto,
  EmployeeOverviewReport,
} from '../types';
import { PendingClientTracker } from '../services/analytics/apiTrackers';

class PendingClientsApiCall implements IPendingClientApiCall {
  private restApiService: IRestApiService;

  constructor(restApiService: IRestApiService) {
    this.restApiService = restApiService;
  }

  unsubscribe(ownerId: string, clientId: string): Promise<void> {
    return this.restApiService.put(`pending-clients/${ownerId}/unsubscribe/${clientId}`);
  }

  addClient(ownerId: string, funnelId: string, data: PendingClientDataDTO): Promise<PendingClientDTO> {
    const startTime = Date.now();

    return this.restApiService
      .post(`pending-clients/${ownerId}/${funnelId}`, data)
      .then((result) => {
        PendingClientTracker.trackAddClient({
          ownerId,
          funnelId,
          leadData: data,
          success: true,
          duration: Date.now() - startTime,
        });
        return result;
      })
      .catch((error) => {
        PendingClientTracker.trackAddClient({
          ownerId,
          funnelId,
          leadData: data,
          success: false,
          error: error.message || error,
          duration: Date.now() - startTime,
        });
        throw error;
      });
  }

  updateClient(ownerId: string, funnelId: string, id: string, data: PendingClientDataDTO): Promise<PendingClientDTO> {
    const startTime = Date.now();

    return this.restApiService
      .put(`pending-clients/${ownerId}/${funnelId}/${id}`, data)
      .then((result) => {
        PendingClientTracker.trackUpdateClient({
          ownerId,
          funnelId,
          id,
          leadData: data,
          success: true,
          duration: Date.now() - startTime,
        });
        return result;
      })
      .catch((error) => {
        PendingClientTracker.trackUpdateClient({
          ownerId,
          funnelId,
          id,
          leadData: data,
          success: false,
          error: error.message || error,
          duration: Date.now() - startTime,
        });
        throw error;
      });
  }

  bulkUpdatePendingClients(
    ownerId: string,
    funnelId: string,
    data: BulkUpdatePendingClientDto
  ): Promise<PendingClientDTO> {
    return this.restApiService.patch(`pending-clients/${ownerId}/${funnelId}/bulk`, data);
  }

  addStudioClient(
    ownerId: string,
    funnelId: string,
    studioClientId: string,
    data: PendingClientDataDTO
  ): Promise<PendingClientDTO> {
    const startTime = Date.now();

    return this.restApiService
      .post(`pending-clients/${ownerId}/${funnelId}/studio-client/${studioClientId}`, data)
      .then((result) => {
        PendingClientTracker.trackAddStudioClient({
          ownerId,
          funnelId,
          studioClientId,
          leadData: data,
          success: true,
          duration: Date.now() - startTime,
        });
        return result;
      })
      .catch((error) => {
        PendingClientTracker.trackAddStudioClient({
          ownerId,
          funnelId,
          studioClientId,
          leadData: data,
          success: false,
          error: error.message || error,
          duration: Date.now() - startTime,
        });
        throw error;
      });
  }

  deleteClient(ownerId: string, id: string): Promise<void> {
    const startTime = Date.now();

    return this.restApiService
      .delete(`pending-clients/${ownerId}/${id}`)
      .then((result) => {
        PendingClientTracker.trackDeleteClient({
          ownerId,
          id,
          success: true,
          duration: Date.now() - startTime,
        });
        return result;
      })
      .catch((error) => {
        PendingClientTracker.trackDeleteClient({
          ownerId,
          id,
          success: false,
          error: error.message || error,
          duration: Date.now() - startTime,
        });
        throw error;
      });
  }

  addSaleHistory(ownerId: string, funnelId: string, pendingClientId: string, data: any): Promise<SaleHistoryItemDTO> {
    const startTime = Date.now();
    const url = `pending-clients/${ownerId}/${funnelId}/${pendingClientId}`;

    return this.restApiService
      .post(url, data)
      .then((result) => {
        PendingClientTracker.trackAddSaleHistory({
          ownerId,
          funnelId,
          pendingClientId,
          saleData: data,
          success: true,
          duration: Date.now() - startTime,
        });
        return result;
      })
      .catch((error) => {
        PendingClientTracker.trackAddSaleHistory({
          ownerId,
          funnelId,
          pendingClientId,
          saleData: data,
          success: false,
          error: error.message || error,
          duration: Date.now() - startTime,
        });
        throw error;
      });
  }

  addMultipleSaleHistory(ownerId: string, funnelId: string, data: any): Promise<SaleHistoryDTO> {
    const startTime = Date.now();
    const url = `pending-clients/${ownerId}/${funnelId}/add-multiple-sales-history`;

    return this.restApiService
      .post(url, data)
      .then((result) => {
        PendingClientTracker.trackAddMultipleSaleHistory({
          ownerId,
          funnelId,
          saleData: data,
          success: true,
          duration: Date.now() - startTime,
          salesCount: result?.length || 0,
        });
        return result;
      })
      .catch((error) => {
        PendingClientTracker.trackAddMultipleSaleHistory({
          ownerId,
          funnelId,
          saleData: data,
          success: false,
          error: error.message || error,
          duration: Date.now() - startTime,
        });
        throw error;
      });
  }

  addSaleStatus(ownerId: string, funnelId: string, pendingClientId: string, data: any): Promise<SaleHistoryItemDTO> {
    const startTime = Date.now();
    const url = `pending-clients/${ownerId}/${funnelId}/${pendingClientId}/add-sale-status`;

    return this.restApiService
      .post(url, data)
      .then((result) => {
        PendingClientTracker.trackAddSaleStatus({
          ownerId,
          funnelId,
          pendingClientId,
          statusData: data,
          success: true,
          duration: Date.now() - startTime,
        });
        return result;
      })
      .catch((error) => {
        PendingClientTracker.trackAddSaleStatus({
          ownerId,
          funnelId,
          pendingClientId,
          statusData: data,
          success: false,
          error: error.message || error,
          duration: Date.now() - startTime,
        });
        throw error;
      });
  }

  addMultipleSaleStatus(ownerId: string, funnelId: string, data: any): Promise<SaleHistoryDTO[]> {
    const startTime = Date.now();
    const url = `pending-clients/${ownerId}/${funnelId}/add-multiple-sales-status`;

    return this.restApiService
      .post(url, data)
      .then((result) => {
        PendingClientTracker.trackAddMultipleSaleStatus({
          ownerId,
          funnelId,
          statusData: data,
          success: true,
          duration: Date.now() - startTime,
          statusesCount: result?.length || 0,
        });
        return result;
      })
      .catch((error) => {
        PendingClientTracker.trackAddMultipleSaleStatus({
          ownerId,
          funnelId,
          statusData: data,
          success: false,
          error: error.message || error,
          duration: Date.now() - startTime,
        });
        throw error;
      });
  }

  findByPhone(ownerId: string, funnelId: string, phone: string, excludeClientId?: string): Promise<PendingClientDTO[]> {
    const url = `pending-clients/${ownerId}/find-by-phone`;
    return this.restApiService.post(url, { phone, excludeClientId, funnelId });
  }

  findByEmail(ownerId: string, funnelId: string, email: string, excludeClientId?: string): Promise<PendingClientDTO[]> {
    const url = `pending-clients/${ownerId}/find-by-email`;
    return this.restApiService.post(url, { email, excludeClientId, funnelId });
  }

  generatePin(ownerId: string, phone: string): Promise<any> {
    return this.restApiService.post(`pending-clients/${ownerId}/generate-pin`, { phone });
  }

  getPendingClientsToRemind(ownerId: string, funnels: string[]): Promise<PendingClientDTO[]> {
    return this.restApiService.post(`pending-clients/${ownerId}/client-to-remind`, { funnels });
  }

  deleteReminder(clientId: string, funnelId: string): Promise<void> {
    return this.restApiService.delete(`pending-clients/${clientId}/reminder/${funnelId}`);
  }

  validatePin(ownerId: string, id: string, phone: string, pin: string): Promise<any> {
    return this.restApiService.post(`pending-clients/${ownerId}/${id}/validate-pin`, { phone, pin });
  }

  updateReminder(ownerId: string, funnelId: string, id: string, reminder?: any): Promise<void> {
    if (!reminder) {
      reminder = new Date();
    }
    const url = `pending-clients/${ownerId}/${funnelId}/${id}/reminder`;
    return this.restApiService.put(url, { reminder: reminder.format('DD-MM-YYYY') });
  }

  getClients(ownerId: string, filter: PendingClientFilter): Promise<{ items: PendingClientDTO[]; total: number }> {
    return this.restApiService.post(`pending-clients/${ownerId}/find`, cleanFilter(filter));
  }

  getPendingClientsByIds(ownerId: string, ids: string[]): Promise<PendingClientDTO[]> {
    const url = `pending-clients/${ownerId}/find-by-ids`;
    return this.restApiService.post(url, { ids });
  }

  getMessages(ownerId: string, id: string): Promise<any[]> {
    const url = `messages/${ownerId}/pending-client/${id}`;
    return this.restApiService.get(url);
  }

  getClientsList<CustomLeadType = { _id: string }>(
    ownerId: string,
    data: { filter: PendingClientFilter; projection: any }
  ): Promise<{ items: CustomLeadType[]; total: number }> {
    return this.restApiService.post(`pending-clients/${ownerId}/find-list`, {
      ...data,
      filter: cleanFilter(data.filter),
    });
  }

  exportClientsList(ownerId: string, data: { filter: PendingClientFilter; columns: string[] }): Promise<Blob> {
    return this.restApiService.doRequest(
      `pending-clients/${ownerId}/export-list`,
      'POST',
      { ...data, filter: cleanFilter(data.filter) },
      undefined,
      { responseType: 'blob' }
    );
  }

  getClientsKanban(ownerId: string, data: LeadsKanbanViewFilter): Promise<LeadsKanbanViewDto> {
    return this.restApiService.post(`pending-clients/${ownerId}/find-kanban`, {
      ...data,
      filter: cleanFilter(data.filter),
    });
  }

  getTags(ownerId: string): Promise<string[]> {
    const params = new URLSearchParams({ dictionaryKey: 'tags' });

    return this.restApiService.get(`pending-clients/sales-funnel/dictionary/values?${params.toString()}`);
  }

  getSources(ownerId: string): Promise<string[]> {
    const params = new URLSearchParams({ dictionaryKey: 'sources' });

    return this.restApiService.get(`pending-clients/sales-funnel/dictionary/values?${params.toString()}`);
  }

  getClient(ownerId: string, id: string): Promise<PendingClientDTO> {
    return this.restApiService.get(`pending-clients/${ownerId}/${id}`);
  }

  getPendingClient(
    ownerId: string,
    client: { _id?: string; phone?: string; email?: string }
  ): Promise<PendingClientDTO> {
    const url = `pending-clients/${ownerId}/find-studio-client`;
    return this.restApiService.post(url, { studioClientId: client._id, phone: client.phone, email: client.email });
  }

  getSaleHistory(ownerId: string, pendingClientId: string): Promise<SaleHistoryDTO> {
    return this.restApiService.get(`pending-clients/${ownerId}/history/${pendingClientId}`);
  }

  getEmployeeDashboard(ownerId: string, params: PendingClientDashboardParams): Promise<any> {
    return this.restApiService.post(`pending-clients/${ownerId}/dashboard/employees`, params);
  }

  getSourceDashboard(ownerId: string, params: PendingClientDashboardParams): Promise<any> {
    return this.restApiService.post(`pending-clients/${ownerId}/dashboard/source`, params);
  }

  getTagsDashboard(ownerId: string, params: PendingClientDashboardParams): Promise<any> {
    return this.restApiService.post(`pending-clients/${ownerId}/dashboard/tags`, params);
  }

  getStudioDashboard(ownerId: string, params: PendingClientDashboardParams): Promise<any> {
    return this.restApiService.post(`pending-clients/${ownerId}/dashboard/studio`, params);
  }

  getStepsDashboard(ownerId: string, params: PendingClientDashboardParams): Promise<any> {
    return this.restApiService.post(`pending-clients/${ownerId}/dashboard/current-steps`, params);
  }

  getStepsOverviewDashboard(ownerId: string, params: PendingClientDashboardParams): Promise<any> {
    return this.restApiService.post(`pending-clients/${ownerId}/dashboard/steps-overview`, params);
  }

  getStepsPreviousStageReport(ownerId: string, params: PendingClientDashboardParams): Promise<any> {
    const { funnelId, ...body } = params as any;
    return this.restApiService.post(
      `pending-clients/${ownerId}/sales-funnel/${funnelId}/analytics/steps-previous`,
      body
    );
  }

  getStepLeads(
    ownerId: string,
    params: PendingClientDashboardParams & { page: number; limit: number; previousStage?: number }
  ): Promise<any> {
    const { funnelId, ...body } = params as any;
    return this.restApiService.post(`pending-clients/${ownerId}/sales-funnel/${funnelId}/analytics/step-leads`, body);
  }

  getStageStats(ownerId: string, funnelId: string, filter: any): Promise<any> {
    return this.restApiService.post(
      `pending-clients/${ownerId}/sales-funnel/${funnelId}/analytics/stage-stats`,
      cleanFilter(filter)
    );
  }

  getLeadsByPreviousStage(
    ownerId: string,
    funnelId: string,
    params: { stage: number; previousStage: number }
  ): Promise<any> {
    return this.restApiService.post(
      `pending-clients/${ownerId}/sales-funnel/${funnelId}/analytics/leads-by-previous`,
      params
    );
  }

  exportLeads(ownerId: string, funnelId: string, stage: number): Promise<any[]> {
    return this.restApiService.post(`pending-clients/${ownerId}/sales-funnel/${funnelId}/analytics/export`, { stage });
  }

  exportStepStats(ownerId: string, funnelId: string, includeLeads: boolean): Promise<any> {
    const url = `pending-clients/${ownerId}/sales-funnel/${funnelId}/analytics/export-step-stats`;

    return this.restApiService.doRequest(url, 'POST', { includeLeads }, undefined, { responseType: 'blob' });
  }

  getEmployeeOverviewDashboard(ownerId: string, params: PendingClientDashboardParams): Promise<EmployeeOverviewReport> {
    const url = `pending-clients/${ownerId}/dashboard/employee-overview`;
    return this.restApiService.post(url, params);
  }

  getUtmValues(
    ownerId: string,
    filter: any
  ): Promise<{
    utm_source: string[];
    utm_medium: string[];
    utm_campaign: string[];
    utm_term: string[];
    utm_content: string[];
  }> {
    const url = `pending-clients/${ownerId}/utm-values`;
    return this.restApiService.post(url, { filter: cleanFilter(filter) });
  }

  getFieldDashboard(ownerId: string, funnelId: string, field: string, filter: PendingClientFilter): Promise<any> {
    const url = `pending-clients/${ownerId}/field-dashboard/${funnelId}`;
    return this.restApiService.post(url, { key: field, filter: cleanFilter(filter) });
  }

  loadDoc(ownerId: string, url: string): Promise<any> {
    return this.restApiService.post(`pending-clients/${ownerId}/load-doc`, { url });
  }

  uploadDoc(
    ownerId: string,
    docUrl: string,
    mapping: SpreadsheetMapping,
    addAsTag: boolean,
    addAsClientInfo: boolean,
    commonFields: any,
    overwriteDuplicates: boolean
  ): Promise<any> {
    const url = `pending-clients/${ownerId}/upload-doc`;
    const data = { url: docUrl, mapping, addAsTag, addAsClientInfo, commonFields, overwriteDuplicates };
    return this.restApiService.post(url, data, 1000000000);
  }

  addSpreadsheet(ownerId: string, funnelId: string, spreadsheet: string): Promise<SpreadsheetConfig> {
    const url = `pending-clients/${ownerId}/funnel/add-spreadsheet`;
    return this.restApiService.post(url, { funnelId, spreadsheet });
  }

  deleteSpreadsheet(ownerId: string, funnelId: string, spreadsheetId: string): Promise<any> {
    const url = `pending-clients/${ownerId}/funnel/${funnelId}/spreadsheet/${spreadsheetId}`;
    return this.restApiService.delete(url);
  }

  getFunnels(ownerId: string): Promise<any[]> {
    return this.restApiService.get(`sales-funnel/${ownerId}/funnels`);
  }

  getFunnelById(ownerId: string, id: string, includeStats: boolean = false): Promise<any> {
    const params = includeStats ? '?includeStats=true' : '';
    return this.restApiService.get(`pending-clients/${ownerId}/funnel/id/${id}${params}`);
  }

  addFunnel(ownerId: string, data: any): Promise<any> {
    return this.restApiService.post(`pending-clients/${ownerId}/add-funnel`, data);
  }

  updateFunnel(ownerId: string, id: string, data: any): Promise<any> {
    return this.restApiService.put(`pending-clients/${ownerId}/funnel/${id}`, data);
  }

  deleteFunnel(ownerId: string, id: string): Promise<void> {
    return this.restApiService.delete(`sales-funnel/${ownerId}/funnel/${id}`);
  }

  getPendingClientInfos(ownerId: string, filter: PendingClientFilter): Promise<ClientInfo[]> {
    const url = `pending-clients/${ownerId}/infos`;
    return this.restApiService.post(url, { filter: cleanFilter(filter) });
  }

  getAllInfos(ownerId: string, filter: PendingClientFilter): Promise<ClientInfo[]> {
    const url = `pending-clients/${ownerId}/all-infos`;
    return this.restApiService.post(url, { filter: cleanFilter(filter) });
  }
}

function cleanFilter(filter: any): any {
  if (!filter) return filter;

  const newFilter = { ...filter };
  Object.keys(newFilter).forEach((key) => {
    const value = newFilter[key];
    if (value === null || value === undefined || value === '') {
      delete newFilter[key];
    } else if (Array.isArray(value) && value.length === 0) {
      delete newFilter[key];
    } else if (typeof value === 'object' && Object.keys(value).length === 0) {
      delete newFilter[key];
    }
  });

  return newFilter;
}

export default PendingClientsApiCall;
